import React, {useState, useEffect, useRef} from "react";
import {
    Mail as InboxIcon,
    PhoneCall as PhoneIcon,
    Globe as GlobeIcon,
    Send as SendIcon,
    Info as InfoIcon,
    Archive as ArchiveIcon,
    Trash as DelIcon,
    CornerUpLeft as ReplyIcon,
    ChevronDown,
    Plus,
    Search as SearchIcon,
    X, ChevronLeft, ChevronRight,
} from "react-feather";
import {
    getEmailNotificationList,
    getSmsNotificationList,
    getCallLogList, sendEmails,
} from "../../api/OutBoxApi";
import LoadingSpinner from "../LoadingSpinner";
import {useSelector} from "react-redux";
import {toast} from "react-toastify";
import {FORMS, MODULES} from "../../const";

export default function MailBoxModal() {
    const user = useSelector((state) => state.user);
    const [selectedFolder, setSelectedFolder] = useState("inbox");
    const [notifications, setNotifications] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [isComposing, setIsComposing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const contentEditableRef = useRef(null);
    const [isSending, setIsSending] = useState(false);
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const pageSize = 20; // or whatever you prefer
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);
    const [activeIdx, setActiveIdx] = useState(0);
    const [composeData, setComposeData] = useState({
        to: "",
        subject: "",
        body: "",
    });
    const [typeFilters, setTypeFilters] = useState(new Set(["All"]));
    const [fieldFilters, setFieldFilters] = useState(new Set(["All"]));

    // Pemission
    const auth = useSelector((state) => state.user.permissions[MODULES?.Master]);
    const transAuth = useSelector((state) => state.user.permissions[MODULES?.Transaction]);
    const outBoxPermission = transAuth?.[FORMS?.OUTBOX?.OUTBOX?.form];
    const outboxComposeView = outBoxPermission?.COMPOSE?.VIEW;

    const SEARCH_DEBOUNCE = 200;

// small util
    const toggleSet = (setState, value, {allKey = "All"} = {}) => {
        setState(prev => {
            const next = new Set(prev);
            if (value === allKey) return new Set([allKey]);
            next.delete(allKey);
            if (next.has(value)) next.delete(value); else next.add(value);
            if (next.size === 0) return new Set([allKey]);
            return next;
        });
    };

    const escapeReg = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const highlight = (text, q) => {
        if (!q) return text || "";
        const re = new RegExp(`(${escapeReg(q)})`, "ig");
        return (text || "").replace(re, "<mark>$1</mark>");
    };

    const typeOfItem = (item) => item?.type || (
        selectedFolder === "trash" ? "Call" :
            selectedFolder === "important" ? "SMS" : "Email"
    );

    useEffect(() => {
        setPage(1);
    }, [selectedFolder]);

    useEffect(() => {
        const t = setTimeout(() => {
            const q = search.trim().toLowerCase();

            // reset UI when query is empty
            if (!q) {
                setSearchResults([]);
                setActiveIdx(0);
                setSearchOpen(false);
                setSearchLoading(false);
                return;
            }

            // tune as needed
            const MAX_RESULTS   = 20;                // how many hits to show
            const MAX_PAGES     = 10;                // safety cap per type
            const PAGE_SIZE     = pageSize || 20;
            const MIN_QUERY_LEN = 2;

            if (q.length < MIN_QUERY_LEN) {
                setSearchResults([]);
                setSearchOpen(true);
                setSearchLoading(false);
                return;
            }

            const typeSet = typeFilters.has("All") ? new Set(["Email","SMS","Call"]) : typeFilters;

            // fields to search
            const fieldSet = fieldFilters.has("All") ? null : fieldFilters;
            const fields = fieldSet
                ? [
                    fieldSet.has("Title") && "subject",
                    fieldSet.has("Message") && "preview",
                    fieldSet.has("Message") && "body",
                    fieldSet.has("Recipient") && "to",
                    fieldSet.has("Recipient") && "cc",
                    fieldSet.has("Sender") && "sender",
                ].filter(Boolean)
                : ["sender", "subject", "preview", "to", "cc", "body"];

            const inStr = (v) => (v || "").toString().toLowerCase().includes(q);
            const matches = (item) => fields.some((f) => inStr(item[f]));

            // normalizers (reuse your stripHtmlTags)
            const normalizeEmailOrSms = (item) => {
                let to = item.notTo;
                try {
                    const parsed = JSON.parse(item.notEventData || "{}");
                    if (parsed && typeof parsed === "object" && parsed.to) to = parsed.to;
                    else if (typeof parsed === "string") to = parsed;
                } catch {
                    to = item.notEventData || item.notTo;
                }
                return {
                    id: item.notId,
                    sender: item.createdBy || item.user || "System",
                    subject: item.notTitle || "(No subject)",
                    preview: stripHtmlTags(item.notMessage || "").slice(0, 80),
                    time: new Date(item.createdDate).toLocaleString(),
                    body: item.notMessage || "",
                    to: to || "N/A",
                    cc: item.notCc || "",
                    type: "Email", // will set to SMS when needed
                };
            };

            const normalizeCall = (item) => ({
                id: item.calId || item.callId,
                sender: item.calCreatedBy || "System",
                subject: item.calNotes ? `Call: ${item.calNotes}` : `Call from job ${item.calJobId}`,
                preview: item.calNotes ? item.calNotes.slice(0, 80) : "No notes",
                time: new Date(item.createdDate).toLocaleString(),
                body: item.calNotes || "No details provided.",
                to: `Job ID: ${item.calJobId}` || "N/A",
                cc: "",
                type: "Call",
            });

            let aborted = false;
            setSearchLoading(true);
            setSearchOpen(true);
            setActiveIdx(0);
            setSearchResults([]);

            const finish = (arr) => {
                if (aborted) return;
                setSearchResults(arr.slice(0, MAX_RESULTS));
                setActiveIdx(0);
                setSearchOpen(true);
                setSearchLoading(false);
            };

            // page-by-page for Email/SMS
            const searchPaged = async (kind) => {
                const hits = [];
                for (let pageIdx = 0; pageIdx < MAX_PAGES; pageIdx++) {
                    if (aborted) break;
                    const offset = pageIdx * PAGE_SIZE;
                    try {
                        const res =
                            kind === "Email"
                                ? await getEmailNotificationList(offset, PAGE_SIZE)
                                : await getSmsNotificationList(offset, PAGE_SIZE);

                        const rows = (res?.data || [])
                            .map(normalizeEmailOrSms)
                            .map((r) => ({ ...r, type: kind }));

                        for (const r of rows) {
                            if (matches(r)) hits.push(r);
                            if (hits.length >= MAX_RESULTS) break;
                        }

                        // stop if short page or enough hits
                        if (rows.length < PAGE_SIZE || hits.length >= MAX_RESULTS) break;
                    } catch (e) {
                        console.error(`Search ${kind} page ${pageIdx} failed`, e);
                        break; // soft-fail
                    }
                }
                return hits;
            };

            // calls (no paging)
            const searchCalls = async () => {
                try {
                    const res = await getCallLogList();
                    const rows = (res?.data || []).map(normalizeCall);
                    return rows.filter(matches).slice(0, MAX_RESULTS);
                } catch (e) {
                    console.error("Search Calls failed", e);
                    return [];
                }
            };

            (async () => {
                const tasks = [];
                if (typeSet.has("Email")) tasks.push(searchPaged("Email"));
                if (typeSet.has("SMS")) tasks.push(searchPaged("SMS"));
                if (typeSet.has("Call")) tasks.push(searchCalls());

                const results = await Promise.all(tasks);

                // score: subject>preview>sender/to/cc>body; then recent first
                const scored = []
                    .concat(...results)
                    .map((r) => {
                        const subjHit = inStr(r.subject);
                        const previewHit = inStr(r.preview);
                        const senderHit = inStr(r.sender);
                        const toHit = inStr(r.to) || inStr(r.cc);
                        const bodyHit = inStr(r.body);
                        const score =
                            (subjHit ? 5 : 0) +
                            (previewHit ? 3 : 0) +
                            (senderHit ? 2 : 0) +
                            (toHit ? 2 : 0) +
                            (bodyHit ? 1 : 0);
                        return { r, score, t: new Date(r.time).getTime() || 0 };
                    })
                    .sort((a, b) => (b.score - a.score) || (b.t - a.t))
                    .map(({ r }) => r);

                finish(scored);
            })();

            return () => {
                aborted = true;
                setSearchLoading(false);
            };
        }, SEARCH_DEBOUNCE);

        return () => clearTimeout(t);
    }, [search, typeFilters, fieldFilters, pageSize]);



// close search when folder changes
    useEffect(() => {
        setSearch("");
        setSearchResults([]);
        setSearchOpen(false);
    }, [selectedFolder]);

// keyboard shortcuts: "/" or Cmd/Ctrl+K to focus, Esc to close
    const searchInputRef = useRef(null);
    // ✅ fixed
    useEffect(() => {
        const onKey = (e) => {
            const active = document.activeElement;
            const isTypingInInput =
                !!active &&
                (["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName) || active.isContentEditable);

            // safely normalize the key
            const key = typeof e.key === "string" ? e.key.toLowerCase() : "";

            if ((key === "/" || (key === "e" && (e.metaKey || e.ctrlKey))) && !isTypingInInput) {
                e.preventDefault();
                searchInputRef.current?.focus();
                setSearchOpen(true);
            }
            if (key === "escape") {
                setSearchOpen(false);
            }
        };

        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);


    const COLORS = {
        border: "#e1e4e8",
        railHover: "#f0f4f8",
        railActiveBg: "#1a73e8",
        railActiveFg: "#fff",
        rowHover: "#f7f9fc",
        rowActive: "#e8f0fe",
    };

    const markdownToHtml = (md) => ({
        __html: md
            .replace(/^### (.*$)/gim, "<h3>$1</h3>")
            .replace(/^## (.*$)/gim, "<h2>$1</h2>")
            .replace(/^# (.*$)/gim, "<h1>$1</h1>")
            .replace(/\*\*(.*?)\*\*/gim, "<strong>$1</strong>")
            .replace(/\_(.*?)\_/gim, "<em>$1</em>")
            .replace(/\n/g, "<br/>"),
    });

    const stripHtmlTags = (html) => {
        const div = document.createElement("div");
        div.innerHTML = html;
        return div.textContent || div.innerText || "";
    };

    useEffect(() => {
        if (isComposing && contentEditableRef.current && composeData.body) {
            contentEditableRef.current.innerHTML = composeData.body;
        }
    }, [isComposing, composeData.body]);

    useEffect(() => {
        const fetchNotifications = async () => {
            setIsLoading(true);
            try {
                const limit  = pageSize;                 // 20
                const offset = (page - 1) * pageSize;    // 0, 20, 40, ...

                let res;
                if (selectedFolder === "inbox") {
                    res = await getEmailNotificationList(offset, limit);
                } else if (selectedFolder === "sms") {
                    res = await getSmsNotificationList(offset, limit);
                } else if (selectedFolder === "call") {
                    res = await getCallLogList(); // calls: no paging
                }

                const formatted = (res?.data || []).map((item) => {
                    let to = item.notTo;

                    if (selectedFolder === "call") {
                        return {
                            id: item.calId || item.callId,
                            sender: item.calCreatedBy || "System",
                            subject: item.calNotes ? `Call: ${item.calNotes}` : `Call from job ${item.calJobId}`,
                            preview: item.calNotes ? item.calNotes.slice(0, 80) : "No notes",
                            time: new Date(item.createdDate).toLocaleString(),
                            body: item.calNotes || "No details provided.",
                            to: `Job ID: ${item.calJobId}` || "N/A",
                            cc: "",
                        };
                    }

                    try {
                        const parsed = JSON.parse(item.notEventData || "{}");
                        if (parsed && typeof parsed === "object" && parsed.to)      to = parsed.to;
                        else if (typeof parsed === "string")                        to = parsed;
                    } catch {
                        to = item.notEventData || item.notTo;
                    }

                    return {
                        id: item.notId || item.callId,
                        sender: item.createdBy || item.user || "System",
                        subject: item.notTitle || `Call from ${item.phoneNumber}`,
                        preview: stripHtmlTags(item.notMessage || item.notes || "").slice(0, 80),
                        time: new Date(item.createdDate).toLocaleString(),
                        body: item.notMessage || item.notes || "",
                        to: to || "N/A",
                        cc: item.notCc || "",
                    };
                });

                setNotifications(formatted);
                setSelectedNotification(formatted[0] || null);
            } catch (e) {
                console.error("Notification fetch error", e);
            } finally {
                setIsLoading(false);
            }
        };

        fetchNotifications();
    }, [selectedFolder, page]);   // 👈 include page here



    const isHtml = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

    const ButtonSpinner = () => (
        <svg width="16" height="16" viewBox="0 0 50 50" aria-hidden>
            <circle cx="25" cy="25" r="20" fill="none" stroke="#fff" strokeWidth="6" opacity="0.2"/>
            <path fill="none" stroke="#fff" strokeWidth="6" d="M45 25c0-11.046-8.954-20-20-20">
                <animateTransform attributeName="transform" type="rotate" from="0 25 25" to="360 25 25" dur="0.9s"
                                  repeatCount="indefinite"/>
            </path>
        </svg>
    );

    const parseRecipients = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value.filter(Boolean).map(String).map(s => s.trim()).filter(Boolean);

        // split on commas/semicolons/newlines
        return String(value)
            .split(/[,;\n]+/)
            .map(s => s.trim())
            .filter(Boolean);
    };

// extract initials for a small avatar-like chip
    const initialsOf = (s) => {
        // "Jane Doe <jane@x.com>" -> "JD"
        const withinAngle = s.match(/<(.*?)>/);
        const addr = withinAngle ? withinAngle[1] : s;

        const namePart = s.replace(/<.*?>/g, "").trim(); // strip <email>
        const name = namePart && /@/.test(namePart) ? "" : namePart;

        if (name) {
            const letters = name.split(/\s+/).filter(Boolean).map(w => w[0]).join("").slice(0, 2);
            return letters.toUpperCase() || (addr[0] || "").toUpperCase();
        }
        return (addr[0] || "").toUpperCase();
    };

    // Reusable chips-with-tooltip component
    function RecipientChips({label, value, maxVisible = 3}) {
        const recipients = parseRecipients(value);
        const [open, setOpen] = React.useState(false);
        const [pinned, setPinned] = React.useState(false);
        const leaveTimer = React.useRef(null);
        const triggerRef = React.useRef(null);

        const clearTimer = () => {
            if (leaveTimer.current) {
                clearTimeout(leaveTimer.current);
                leaveTimer.current = null;
            }
        };

        const openWithIntent = () => {
            clearTimer();
            setOpen(true);
        };

        const closeWithIntent = () => {
            // small delay so you can move cursor into the popover without it disappearing
            clearTimer();
            leaveTimer.current = setTimeout(() => {
                if (!pinned) setOpen(false);
            }, 160);
        };

        React.useEffect(() => () => clearTimer(), []);

        if (recipients.length === 0) {
            return (
                <div style={{display: "flex", gap: 6, alignItems: "center"}}>
                    <strong style={{color: "#555"}}>{label}:</strong>
                    <span style={{color: "#999"}}>—</span>
                </div>
            );
        }

        const visible = recipients.slice(0, maxVisible);
        const overflow = recipients.length - visible.length;

        return (
            <div style={{position: "relative", display: "flex", alignItems: "center", gap: 6}}>
                <strong style={{color: "#555", flexShrink: 0}}>{label}:</strong>

                <div style={{display: "flex", gap: 6, flexWrap: "wrap"}}>
                    {visible.map((r, idx) => (
                        <div
                            key={`${r}-${idx}`}
                            title={r}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                border: "1px solid #e1e4e8",
                                borderRadius: 999,
                                padding: "2px 8px 2px 4px",
                                background: "#f7f9fc",
                                fontSize: 12,
                            }}
                        >
                            <div
                                aria-hidden
                                style={{
                                    width: 18, height: 18, borderRadius: "50%",
                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                    fontSize: 10, fontWeight: 700, background: "#e8f0fe", border: "1px solid #d6e2ff",
                                }}
                            >
                                {initialsOf(r)}
                            </div>
                            <span style={{
                                maxWidth: 160,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap"
                            }}>
              {r}
            </span>
                        </div>
                    ))}

                    {overflow > 0 && (
                        <div
                            ref={triggerRef}
                            role="button"
                            aria-haspopup="dialog"
                            aria-expanded={open || pinned}
                            title={`${overflow} more`}
                            onMouseEnter={openWithIntent}
                            onMouseLeave={closeWithIntent}
                            onClick={() => {
                                clearTimer();
                                setPinned(p => {
                                    const next = !p;
                                    if (!next) setOpen(false);
                                    else setOpen(true);
                                    return next;
                                });
                            }}
                            style={{
                                border: "1px solid #e1e4e8",
                                borderRadius: 999,
                                padding: "2px 8px",
                                background: "#fff",
                                fontSize: 12,
                                cursor: "pointer",
                                userSelect: "none",
                            }}
                        >
                            +{overflow}
                        </div>
                    )}
                </div>

                {/* Popover */}
                {(open || pinned) && overflow > 0 && (
                    <div
                        role="dialog"
                        aria-label={`${label} recipients`}
                        onMouseEnter={openWithIntent}
                        onMouseLeave={closeWithIntent}
                        style={{
                            position: "absolute",
                            top: "100%",
                            left: 0,            // aligns with the chips; easy to reach
                            marginTop: 4,       // smaller gap = easier to enter
                            minWidth: 360,
                            maxWidth: 480,
                            maxHeight: 300,
                            overflow: "auto",
                            background: "#fff",
                            border: "1px solid #e1e4e8",
                            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                            borderRadius: 8,
                            padding: 10,
                            zIndex: 9999,
                        }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: 8
                        }}>
                            <div style={{fontSize: 12, fontWeight: 600, color: "#444"}}>
                                {label} ({recipients.length})
                            </div>
                            <button
                                onClick={() => {
                                    setPinned(false);
                                    setOpen(false);
                                }}
                                style={{
                                    border: "none", background: "transparent", cursor: "pointer",
                                    fontSize: 12, color: "#666", padding: 4
                                }}
                                title="Close"
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{display: "grid", gridTemplateColumns: "auto 1fr", gap: "8px 10px"}}>
                            {recipients.map((r, i) => (
                                <React.Fragment key={`${r}-${i}`}>
                                    <div
                                        style={{
                                            width: 22,
                                            height: 22,
                                            borderRadius: "50%",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontSize: 11,
                                            fontWeight: 700,
                                            background: "#e8f0fe",
                                            border: "1px solid #d6e2ff",
                                        }}
                                    >
                                        {initialsOf(r)}
                                    </div>
                                    <div
                                        title={r}
                                        style={{
                                            minWidth: 0, fontSize: 13, paddingBottom: 6,
                                            borderBottom: "1px dashed #f0f0f0", color: "#222",
                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                        }}
                                    >
                                        {r}
                                    </div>
                                </React.Fragment>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    }


    return (
        <div style={{display: "flex", flexDirection: "column", height: "100%"}}>
            {/* Header: Compose */}
            <div
                style={{
                    width: '100%',
                    display: "flex",
                    alignItems: "center",
                    justifyContent: 'space-between',
                    padding: "12px 20px",
                    borderBottom: `1px solid ${COLORS.border}`,
                    gap: 12
                }}
            >
                <h3 style={{margin: 0, whiteSpace: "nowrap"}}>OUTBOX</h3>

                {/* Search box */}
                <div style={{position: "relative", flex: 1, maxWidth: 720}}>
                    <SearchIcon size={16} style={{
                        position: "absolute",
                        left: 10,
                        top: "50%",
                        transform: "translateY(-50%)",
                        opacity: 0.6
                    }}/>
                    <input
                        ref={searchInputRef}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        onFocus={() => search && setSearchOpen(true)}
                        placeholder="Search mail & SMS — try subject, recipient, or content"
                        style={{
                            width: "100%",
                            padding: "8px 28px 8px 30px",
                            border: `1px solid ${COLORS.border}`,
                            borderRadius: 8,
                            fontSize: 14
                        }}
                    />

                    {!!search && (
                        <button
                            onClick={() => {
                                setSearch("");
                                setSearchOpen(false);
                            }}
                            title="Clear"
                            style={{
                                position: "absolute",
                                right: 6,
                                top: "50%",
                                transform: "translateY(-50%)",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                                fontSize: 16,
                                color: "#666"
                            }}
                        >
                            ×
                        </button>
                    )}

                    {/* Results panel */}
                    {searchOpen && (
                        <div
                            onMouseDown={(e) => e.preventDefault()} // keep input focus while clicking
                            style={{
                                position: "absolute",
                                top: "calc(100% + 8px)",
                                left: 0,
                                right: 0,
                                background: "#fff",
                                border: `1px solid ${COLORS.border}`,
                                boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
                                borderRadius: 10,
                                overflow: "hidden",
                                zIndex: 20
                            }}
                        >
                            {/* header */}
                            <div
                                style={{
                                    padding: "8px 10px",
                                    fontSize: 12,
                                    color: "#555",
                                    background: "#fafbfc",
                                    borderBottom: `1px solid ${COLORS.border}`,
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    gap: 8
                                }}
                            >
                                <div>
                                    {searchLoading ? "Searching…" : (
                                        <>
                                            Results in <strong style={{fontWeight: 600}}>
                                            {selectedFolder === "inbox"
                                                ? "Email"
                                                : selectedFolder === "important"
                                                    ? "SMS"
                                                    : "Calls"}
                                        </strong> — {searchResults.length} shown
                                        </>
                                    )}
                                </div>


                                {/* tiny “X” to close */}
                                <button
                                    onClick={() => setSearchOpen(false)}
                                    style={{
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        color: "#666",
                                        fontSize: 16,
                                        lineHeight: 1,
                                        padding: 2
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {/* scroll area that contains a sticky filter bar + results */}
                            <div
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowDown") {
                                        e.preventDefault();
                                        setActiveIdx((i) => Math.min(i + 1, Math.max(searchResults.length - 1, 0)));
                                    }
                                    if (e.key === "ArrowUp") {
                                        e.preventDefault();
                                        setActiveIdx((i) => Math.max(i - 1, 0));
                                    }
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        const item = searchResults[activeIdx];
                                        if (item) {
                                            setSelectedNotification(item);
                                            setSearchOpen(false);
                                        }
                                    }
                                    if (e.key === "Escape") {
                                        setSearchOpen(false);
                                    }
                                }}
                                tabIndex={0}
                                style={{
                                    maxHeight: 360,
                                    overflow: "auto",
                                    outline: "none"
                                }}
                            >
                                {/* sticky filter bar */}
                                <div
                                    style={{
                                        position: "sticky",
                                        top: 0,
                                        zIndex: 1,
                                        background: "#fff",
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        padding: "8px 10px",
                                        display: "grid",
                                        gridTemplateColumns: "auto 1fr auto",
                                        alignItems: "center",
                                        gap: 10
                                    }}
                                >
                                    {/* Type segmented */}
                                    <div style={{
                                        display: "inline-flex",
                                        border: `1px solid ${COLORS.border}`,
                                        borderRadius: 999,
                                        overflow: "hidden"
                                    }}>
                                        {[
                                            {key: "All", label: "All"},
                                            {key: "Email", label: "Email"},
                                            {key: "SMS", label: "SMS"},
                                            {key: "Call", label: "Call"}
                                        ].map(({key, label}, idx, arr) => {
                                            const on = typeFilters.has(key);
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => toggleSet(setTypeFilters, key)}
                                                    style={{
                                                        padding: "6px 10px",
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        border: "none",
                                                        borderRight: idx < arr.length - 1 ? `1px solid ${COLORS.border}` : "none",
                                                        background: on ? "#e8f0fe" : "#fff",
                                                        color: on ? "#1a73e8" : "#333",
                                                        cursor: "pointer"
                                                    }}
                                                >
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* In: chips */}
                                    <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6}}>
                                        <span style={{fontSize: 12, color: "#666"}}>In:</span>
                                        {["All", "Title", "Message", "Recipient", "Sender"].map((key) => {
                                            const on = fieldFilters.has(key);
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => toggleSet(setFieldFilters, key)}
                                                    style={{
                                                        borderRadius: 999,
                                                        padding: "4px 10px",
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        cursor: "pointer",
                                                        border: on ? "1px solid #0f9d58" : `1px solid ${COLORS.border}`,
                                                        background: on ? "#e6f4ea" : "#fff",
                                                        color: on ? "#0f9d58" : "#333"
                                                    }}
                                                >
                                                    {key}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Clear filters */}
                                    {(!typeFilters.has("All") || !fieldFilters.has("All")) ? (
                                        <button
                                            onClick={() => {
                                                setTypeFilters(new Set(["All"]));
                                                setFieldFilters(new Set(["All"]));
                                            }}
                                            style={{
                                                border: "none",
                                                background: "transparent",
                                                color: "#666",
                                                fontSize: 12,
                                                cursor: "pointer",
                                                justifySelf: "end",
                                                padding: 4
                                            }}
                                        >
                                            Clear
                                        </button>
                                    ) : (
                                        <span/>
                                    )}
                                </div>

                                {/* results list OR empty state */}
                                {searchResults.length > 0 ? (
                                    searchResults.map((it, i) => {
                                        const t = typeOfItem(it);
                                        const isActive = i === activeIdx;
                                        const TypeIcon =
                                            t === "Email" ? InboxIcon : t === "SMS" ? GlobeIcon : PhoneIcon;
                                        const badgeBg = t === "Email" ? "#e8f0fe" : t === "SMS" ? "#e6f4ea" : "#f3e8ff";
                                        const badgeBdr = t === "Email" ? "#c7d2fe" : t === "SMS" ? "#b7e0c2" : "#e3d0ff";
                                        const badgeTxt = t === "Email" ? "#1a73e8" : t === "SMS" ? "#0f9d58" : "#7e57c2";

                                        return (
                                            <div
                                                key={it.id}
                                                onMouseEnter={() => setActiveIdx(i)}
                                                onClick={() => {
                                                    setSelectedNotification(it);
                                                    setSearchOpen(false);
                                                }}
                                                style={{
                                                    display: "grid",
                                                    gridTemplateColumns: "24px 1fr auto",
                                                    alignItems: "center",
                                                    gap: 10,
                                                    padding: "10px 12px",
                                                    background: isActive ? "#f6fafe" : "#fff",
                                                    cursor: "pointer",
                                                    borderBottom: `1px solid ${COLORS.border}`
                                                }}
                                            >
                                                <div style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    opacity: 0.8
                                                }}>
                                                    <TypeIcon size={16}/>
                                                </div>

                                                <div style={{minWidth: 0}}>
                                                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                                        <div
                                                            style={{
                                                                border: `1px solid ${badgeBdr}`,
                                                                background: badgeBg,
                                                                color: badgeTxt,
                                                                borderRadius: 999,
                                                                fontSize: 10,
                                                                fontWeight: 700,
                                                                padding: "2px 8px",
                                                                lineHeight: 1
                                                            }}
                                                        >
                                                            {t}
                                                        </div>
                                                        <div
                                                            style={{
                                                                fontWeight: 600,
                                                                minWidth: 0,
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap"
                                                            }}
                                                            dangerouslySetInnerHTML={{
                                                                __html: highlight(it.subject || "(No subject)", search)
                                                            }}
                                                        />
                                                    </div>
                                                    <div
                                                        style={{
                                                            fontSize: 12,
                                                            color: "#555",
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap"
                                                        }}
                                                        dangerouslySetInnerHTML={{__html: highlight(it.preview || "", search)}}
                                                    />
                                                    <div style={{fontSize: 11, color: "#777"}}>
                                                        {it.sender} • To: {it.to}
                                                    </div>
                                                </div>

                                                <div style={{
                                                    fontSize: 11,
                                                    color: "#999",
                                                    marginLeft: 8,
                                                    whiteSpace: "nowrap"
                                                }}>
                                                    {it.time}
                                                </div>
                                            </div>
                                        );
                                    })
                                ) : (
                                    <div
                                        style={{
                                            padding: "28px 16px",
                                            textAlign: "center",
                                            color: "#666",
                                            fontSize: 13
                                        }}
                                    >
                                        {searchLoading ?"Searching...." : (
                                            "No results. Try different filters or keywords."
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* footer hint */}
                            <div
                                style={{
                                    padding: "8px 10px",
                                    fontSize: 11,
                                    color: "#666",
                                    background: "#fafbfc"
                                }}
                            >
                                Tip: ↑/↓ to navigate, Enter to open, Esc to close
                            </div>
                        </div>
                    )}

                </div>

                {/* Compose button (unchanged) */}
                {outboxComposeView ? (
                    <button
                        onClick={() => setIsComposing(true)}
                        style={{
                            background: "#1a73e8",
                            color: "#fff",
                            border: "none",
                            padding: "8px 14px",
                            borderRadius: 4,
                            fontWeight: 500,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            whiteSpace: "nowrap"
                        }}
                    >
                        <Plus size={16} style={{marginRight: 6}}/>
                        Compose
                    </button>
                ) : (
                    <div style={{
                        background: "#fff",
                        color: "#fff",
                        border: "none",
                        padding: "8px 14px",
                        borderRadius: 4,
                        fontWeight: 500,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        whiteSpace: "nowrap"
                    }}></div>
                )}
            </div>


            <div style={{
                display: "flex",
                height: "calc(100vh - 60px)",
                minWidth: 760,
                overflow: "hidden",
                minHeight: 0
            }}>
                {/* Sidebar */}
                <div
                    style={{
                        width: 54,
                        borderRight: `1px solid ${COLORS.border}`,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        paddingTop: 12,
                    }}
                >
                    {[
                        {id: "inbox", icon: InboxIcon},
                        {id: "call", icon: PhoneIcon},
                        {id: "sms", icon: GlobeIcon},
                    ].map((item) => {
                        const Active = selectedFolder === item.id;
                        const IconTag = item.icon;
                        return (
                            <div
                                key={item.id}
                                onClick={() => setSelectedFolder(item.id)}
                                style={{
                                    width: 34,
                                    height: 34,
                                    marginBottom: 12,
                                    borderRadius: 6,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    background: Active ? COLORS.railActiveBg : "transparent",
                                    color: Active ? COLORS.railActiveFg : "#333",
                                    cursor: "pointer",
                                }}
                                title={item.id}
                            >
                                <IconTag size={18}/>
                            </div>
                        );
                    })}
                </div>

                {/* Email/SMS/Call List Column */}
                <div
                    style={{
                        width: 300,
                        borderRight: `1px solid ${COLORS.border}`,
                        display: "flex",
                        flexDirection: "column",
                        minHeight: 0,
                        flexShrink: 0,
                        background: "#fff",
                    }}
                >
                    {/* Scrollable list area (the pager lives inside here and is sticky) */}
                    <div style={{ flex: "1 1 auto", overflowY: "auto", minHeight: 0 }}>
                        {/* rows */}
                        {notifications.map((item) => {
                            const isActive = item.id === selectedNotification?.id;
                            return (
                                <div
                                    key={item.id}
                                    onClick={() => setSelectedNotification(item)}
                                    style={{
                                        background: isActive ? COLORS.rowActive : "#fff",
                                        padding: "10px 12px",
                                        borderBottom: `1px solid ${COLORS.border}`,
                                        cursor: "pointer",
                                    }}
                                >
                                    <div style={{ fontWeight: 600, color: "#222" }}>{item.sender}</div>
                                    <div style={{ fontSize: 13, fontWeight: 500, color: "#111" }}>{item.subject}</div>
                                    <div style={{ fontSize: 12, color: "#555" }}>{item.preview}</div>
                                    <div style={{ fontSize: 11, color: "#888" }}>{item.time}</div>
                                </div>
                            );
                        })}

                        <div
                            style={{
                                position: "sticky",
                                bottom: 75,
                                zIndex: 1,
                                background: "rgba(250,251,252,0.98)",
                                backdropFilter: "blur(2px)",
                                borderTop: `1px solid ${COLORS.border}`,
                                padding: 10,
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: 6,
                                boxShadow: "0 -6px 12px rgba(0,0,0,0.06)",

                            }}
                        >
                            {/* Prev */}
                            <button
                                disabled={page === 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: 999,
                                    border: "1px solid #d0d7de",
                                    background: "#fff",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: page === 1 ? "not-allowed" : "pointer",
                                    opacity: page === 1 ? 0.5 : 1,
                                }}
                            >
                                <ChevronLeft/>
                            </button>

                            {/* Numbered buttons (centered around current) */}
                            {Array.from({ length: 5 }, (_, i) => page - 2 + i)
                                .filter((n) => n >= 1)
                                .map((pg) => {
                                    const active = pg === page;
                                    return (
                                        <button
                                            key={pg}
                                            onClick={() => setPage(pg)}
                                            aria-current={active ? "page" : undefined}
                                            style={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                border: active ? "2px solid #1a73e8" : "1px solid #d0d7de",
                                                background: active ? "#e8f0fe" : "#fff",
                                                color: active ? "#1a73e8" : "#333",
                                                fontWeight: active ? 700 : 500,
                                                fontSize: 13,
                                                cursor: "pointer",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                            }}
                                        >
                                            {pg}
                                        </button>
                                    );
                                })}

                            {/* Next */}
                            <button
                                disabled={notifications.length < pageSize}
                                onClick={() => setPage((p) => p + 1)}
                                style={{
                                    padding: "6px 12px",
                                    borderRadius: 999,
                                    border: "1px solid #d0d7de",
                                    background: "#fff",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    cursor: notifications.length < pageSize ? "not-allowed" : "pointer",
                                    opacity: notifications.length < pageSize ? 0.5 : 1,
                                }}
                            >
                                <ChevronRight/>
                            </button>
                        </div>
                    </div>
                </div>



                {/* Preview Panel */}
                <div style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: "auto",
                    padding: "16px 28px",
                    background: "#fff"
                }}>
                    {isLoading ? (
                        <LoadingSpinner/>
                    ) : (
                        selectedNotification && (
                            <>
                                <div
                                    style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        marginBottom: 10,
                                        fontSize: 13,
                                    }}
                                >
                                    {/* Left Side: To + CC */}
                                    <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                        <RecipientChips label="To" value={selectedNotification.to}/>
                                        <RecipientChips label="CC" value={selectedNotification.cc}/>
                                    </div>

                                    {/* Right Side: Time + Resend */}
                                    <div style={{
                                        textAlign: "right",
                                        display: "flex",
                                        flexDirection: "row",
                                        alignItems: "center",
                                        gap: 28
                                    }}>
  <span style={{fontSize: 12, color: "#888", marginBottom: 4}}>
    {selectedNotification.time}
  </span>
                                        <SendIcon
                                            size={16}
                                            style={{cursor: "pointer", color: "#1a73e8"}}
                                            title="Resend"
                                            onClick={() => {
                                                const originalHTML = isHtml(selectedNotification.body)
                                                    ? selectedNotification.body
                                                    : markdownToHtml(selectedNotification.body).__html;

                                                const formattedBody = `
        <br/><br/>
        ---------- Resending message ---------<br/>
        <b>From:</b> ${selectedNotification.sender}<br/>
        <b>Date:</b> ${selectedNotification.time}<br/>
        <b>To:</b> ${selectedNotification.to}<br/>
        <b>Subject:</b> ${selectedNotification.subject}<br/><br/>
        <div style="margin-left:20px; padding:10px; border-left:2px solid #ccc; background:#f9f9f9;">
            ${originalHTML}
        </div>
    `;

                                                setComposeData({
                                                    to: selectedNotification.to,
                                                    subject: `Fwd: ${selectedNotification.subject || ""}`,
                                                    body: formattedBody,
                                                });

                                                setIsComposing(true);
                                            }}

                                        />
                                    </div>
                                </div>
                                <div
                                    style={{
                                        border: "1px solid #ddd",
                                        borderRadius: 6,
                                        padding: 16,
                                        background: "#fff",
                                        overflowX: "auto",
                                    }}
                                    dangerouslySetInnerHTML={{
                                        __html: isHtml(selectedNotification.body)
                                            ? selectedNotification.body
                                            : markdownToHtml(selectedNotification.body).__html,
                                    }}
                                />


                            </>
                        )
                    )}
                </div>
            </div>

            {/* Compose Modal */}
            {isComposing && (
                <div
                    style={{
                        position: "fixed",
                        bottom: 20,
                        right: 20,
                        width: 500,
                        background: "#fff",
                        borderRadius: 8,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                        zIndex: 9999,
                        display: "flex",
                        flexDirection: "column",
                        maxHeight: "70vh",
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            background: "#f1f3f4",
                            padding: "10px 16px",
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            fontWeight: 500,
                            borderBottom: "1px solid #ddd",
                        }}
                    >
                        New Message
                        <X
                            size={18}
                            onClick={() => {
                                setIsComposing(false);
                                setComposeData({to: "", subject: "", body: ""});
                            }}
                            style={{cursor: "pointer"}}
                        />
                    </div>

                    {/* Body */}
                    <div style={{padding: 16, display: "flex", flexDirection: "column", gap: 10}}>
                        <input
                            placeholder="To"
                            value={composeData.to}
                            onChange={(e) => setComposeData({...composeData, to: e.target.value})}
                            style={{
                                border: "none",
                                borderBottom: "1px solid #ccc",
                                padding: "6px 8px",
                                outline: "none",
                                fontSize: 14,
                            }}
                        />
                        <input
                            placeholder="Subject"
                            value={composeData.subject}
                            onChange={(e) => setComposeData({...composeData, subject: e.target.value})}
                            style={{
                                border: "none",
                                borderBottom: "1px solid #ccc",
                                padding: "6px 8px",
                                outline: "none",
                                fontSize: 14,
                            }}
                        />
                        <div
                            ref={contentEditableRef}
                            contentEditable
                            suppressContentEditableWarning={true}
                            style={{
                                minHeight: 120,
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "10px",
                                background: "#fff",
                                overflow: "auto",
                                fontSize: 14,
                                maxHeight: "40vh",
                            }}
                        />

                    </div>

                    {/* Footer */}
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 16px",
                            borderTop: "1px solid #ddd",
                        }}
                    >
                        <button
                            disabled={isSending}
                            onClick={async () => {
                                if (isSending) return; // guard
                                setIsSending(true);
                                const bodyHTML = contentEditableRef.current?.innerHTML || "";

                                const payload = {
                                    username: user.username,
                                    title: composeData.subject,
                                    body: bodyHTML,
                                    alertType: 4,
                                    data: composeData.to,
                                    eventType: 1,
                                    type: "email",
                                    jobCode: "",
                                    vehicleNo: "",
                                    companyId: 1,
                                };

                                try {
                                    await sendEmails(payload);
                                    toast.success("Email sent successfully!", {containerId: "Modal", autoClose: 5000});
                                    setIsComposing(false);
                                    setComposeData({to: "", subject: "", body: ""});
                                } catch (err) {
                                    console.error("Failed to send email:", err);
                                    toast.error("Failed to send email. Please try again later.", {
                                        containerId: "Modal",
                                        autoClose: 5000
                                    });
                                } finally {
                                    setIsSending(false);
                                }
                            }}
                            style={{
                                background: isSending ? "#6fa8f4" : "#1a73e8",
                                color: "#fff",
                                border: "none",
                                padding: "8px 20px",
                                borderRadius: 20,
                                fontWeight: 500,
                                cursor: isSending ? "not-allowed" : "pointer",
                                fontSize: 14,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 8,
                                opacity: isSending ? 0.9 : 1,
                            }}
                            aria-busy={isSending}
                            aria-live="polite"
                        >
                            {isSending ? (<><ButtonSpinner/> Sending…</>) : "Send"}
                        </button>
                        {/* Optional icons/actions could be placed here like Gmail */}
                    </div>
                </div>
            )}
        </div>
    );
}
